import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthorization } from '../auth/useAuthorization';
import { Role } from '../auth/roles';
import { GAME_LIST } from '../auth/permissions';
import NotificationBanner from './common/NotificationBanner';
import logoImg from '../assets/logo.png';

const CONTACT_NUMBER = '+1 (581) 256-8748';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, hasPermission } = useAuthorization();
  const isAdmin = hasRole(Role.ADMIN);
  const isPartner = hasRole(Role.PARTNER);
  const canOpenSoda = hasPermission(GAME_LIST);

  return (
    <div className="min-h-[calc(100vh-48px)] flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Scrolling Notification */}
      <NotificationBanner />

      {/* Main Content - compact layout to fit on page */}
      <main className="flex-1 flex flex-col items-center justify-center px-3 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 min-h-0">
        <div className="max-w-2xl w-full text-center flex flex-col items-center">
          {/* Tiger/App Icon */}
          <div className="flex justify-center mb-3 sm:mb-4">
            <img
              src={logoImg}
              alt="Tiger Software"
              className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-lg sm:rounded-xl object-contain flex-shrink-0"
            />
          </div>

          {/* Welcome Message */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2 sm:mb-3 leading-tight">
            Welcome to Tiger Software
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-1">
            Your trusted accounting companion
          </p>
          <p className="text-sm sm:text-base text-slate-500 mb-4 sm:mb-5 max-w-lg mx-auto px-1">
            Streamline your financial operations, track transactions, manage party accounts, and generate accurate reports—all in one place.
          </p>

          {/* Contact Number */}
          <div className="mb-5 sm:mb-6">
            <p className="text-xs sm:text-sm text-slate-500 mb-0.5">Contact us</p>
            <a
              href={`tel:${CONTACT_NUMBER.replace(/\s/g, '')}`}
              className="text-base sm:text-lg md:text-xl font-semibold text-blue-600 hover:text-blue-700 transition-colors break-all"
            >
              {CONTACT_NUMBER}
            </a>
          </div>

          {/* Quick Actions — Admin: none; Partner: soda games only; others: full set */}
          {!isAdmin && (canOpenSoda || !isPartner) ? (
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {canOpenSoda ? (
                <>
                  <button
                    onClick={() => navigate('/game/oneday')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    One Day Games
                  </button>
                  <button
                    onClick={() => navigate('/game/test')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    Test Games
                  </button>
                  <button
                    onClick={() => navigate('/game/session')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    Session Games
                  </button>
                </>
              ) : null}
              {!isPartner ? (
                <>
                  <button
                    onClick={() => navigate('/party')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    Party List
                  </button>
                  <button
                    onClick={() => navigate('/report/havala')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    Havala
                  </button>
                  <button
                    onClick={() => navigate('/report/trial')}
                    className="px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 text-sm sm:text-base font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    Trial Balance
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default Home;
export { CONTACT_NUMBER };
